import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { configAPI } from '../../services/api';

const defaultActiveConfigForm = {
  llm_provider: '',
  llm_model_name: '',
  llm_model_key: '',  // Add key field
  llm_model_endpoint: '',
  llm_model_apiversion: '',
  llm_model_temperature: 0.7,
  embedding_provider: '',
  embedding_model_name: '',
  embedding_model_key: '',  // Add key field
  embedding_model_endpoint: '',
  embedding_model_apiversion: '',
  vector_db_provider: '',
  vector_db_name: '',
  vector_db_key: '',  // Add key field
  vector_db_collection_name: '',
  vector_db_endpoint: '',
  pdf_parser: 'unstructured',
  vision_llm_provider: '',
  vision_llm_model_name: '',
};

const defaultSettingsForm = {
  max_documents: 10,
  max_file_size_mb: 10,
  default_temperature: 0.7,
  default_chunk_size: 1000,
  default_chunk_overlap: 200,
};

const PlatformConfigEditModal = ({ isOpen, onClose, config, onSave, editMode = 'active_config', visionModels = {} }) => {
  const [formData, setFormData] = useState(editMode === 'settings' ? defaultSettingsForm : defaultActiveConfigForm);
  const [showKeys, setShowKeys] = useState({
    llm: false,
    embedding: false,
    vector_db: false,
  });
  const [options, setOptions] = useState({
    llm_providers: [],
    llm_models: {},
    embedding_providers: [],
    embedding_models: {},
    vector_db_providers: [],
    vector_dbs: {},
    vision_providers: [],
    vision_models: {},
    pdf_parsers: ['unstructured', 'pymupdf', 'pdfminer'],
  });

  useEffect(() => {
    if (!isOpen) return;
    
    const loadOptions = async () => {
      try {
        const { data } = await configAPI.getActiveSetup();
        // Use new unified config structure: available_options contains all options
        const availableOptions = data.available_options || {};

        const llmProviders = Object.keys(availableOptions.model_options || {});
        const modelOptions = availableOptions.model_options || {};
        const embeddingProviders = Object.keys(availableOptions.embedding_models || {});
        const vectorDbProviders = Object.keys(availableOptions.vector_dbs || {});
        
        const visionModelOptions = availableOptions.vision_models || visionModels || {};
        const visionProviders = Object.keys(visionModelOptions);
        
        const parserSet = new Set(['unstructured', 'pymupdf', 'pdfplumber']);
        if (data.pdf_parser) parserSet.add(data.pdf_parser);

        setOptions({
          llm_providers: llmProviders,
          llm_models: modelOptions,
          embedding_providers: embeddingProviders,
          embedding_models: availableOptions.embedding_models || {},
          vector_db_providers: vectorDbProviders,
          vector_dbs: availableOptions.vector_dbs || {},
          vision_providers: visionProviders,
          vision_models: visionModelOptions,
          pdf_parsers: Array.from(parserSet),
        });
      } catch (error) {
        console.error('Failed loading config options', error);
        toast.error('Failed to load configuration options');
      }
    };
    
    loadOptions();
  }, [isOpen, visionModels]);

  useEffect(() => {
    if (!isOpen || !config) return;
    
    if (editMode === 'settings') {
      setFormData({
        ...defaultSettingsForm,
        ...(config.settings || {}),
      });
    } else {
      // Use unified active_config from new config structure
      const activeConfig = config.active_config || {};
      
      setFormData({
        ...defaultActiveConfigForm,
        ...activeConfig,
      });
    }
  }, [isOpen, config, editMode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear model selection and key when provider changes
    if (field === 'llm_provider') {
      setFormData(prev => ({ ...prev, llm_model_name: '', llm_model_key: '' }));
    }
    if (field === 'embedding_provider') {
      setFormData(prev => ({ ...prev, embedding_model_name: '', embedding_model_key: '' }));
    }
    if (field === 'vector_db_provider') {
      setFormData(prev => ({ ...prev, vector_db_name: '', vector_db_key: '' }));
    }
    if (field === 'vision_llm_provider') {
      setFormData(prev => ({ ...prev, vision_llm_model_name: '' }));
    }
  };

  // Auto-populate key from model_options when model is selected
  const handleModelSelect = (field, value, keyField, modelsDict) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Find the selected model and auto-fill key if available
    const provider = formData[field.replace('_model_name', '_provider')];
    if (provider && modelsDict[provider]) {
      const selectedModel = modelsDict[provider].find(m => m.name === value);
      if (selectedModel && selectedModel.key) {
        setFormData(prev => ({
          ...prev,
          [keyField]: selectedModel.key,
        }));
      }
    }
  };

  const toggleShowKey = (keyType) => {
    setShowKeys(prev => ({
      ...prev,
      [keyType]: !prev[keyType],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">
            {editMode === 'settings' ? 'Edit Platform Settings' : 'Edit Active Configuration'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {editMode === 'active_config' ? (
            <>
              {/* LLM Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">LLM Configuration</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <select
                      value={formData.llm_provider}
                      onChange={(e) => handleInputChange('llm_provider', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Provider</option>
                      {options.llm_providers.map(provider => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <select
                      value={formData.llm_model_name}
                      onChange={(e) => handleModelSelect('llm_model_name', e.target.value, 'llm_model_key', options.llm_models)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Model</option>
                      {formData.llm_provider && (options.llm_models[formData.llm_provider] || []).map(model => (
                        <option key={model.name} value={model.name}>{model.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* API Key field */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                    <span className="ml-2 text-xs text-gray-500">(Required for cloud providers)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showKeys.llm ? 'text' : 'password'}
                      value={formData.llm_model_key || ''}
                      onChange={(e) => handleInputChange('llm_model_key', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter API key"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowKey('llm')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showKeys.llm ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={formData.llm_model_temperature}
                      onChange={(e) => handleInputChange('llm_model_temperature', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint (Optional)</label>
                    <input
                      type="text"
                      value={formData.llm_model_endpoint || ''}
                      onChange={(e) => handleInputChange('llm_model_endpoint', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="API endpoint"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Version (Optional)</label>
                    <input
                      type="text"
                      value={formData.llm_model_apiversion || ''}
                      onChange={(e) => handleInputChange('llm_model_apiversion', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 2024-02-15-preview"
                    />
                  </div>
                </div>
              </div>

              {/* Embedding Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Embedding Configuration</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <select
                      value={formData.embedding_provider}
                      onChange={(e) => handleInputChange('embedding_provider', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Provider</option>
                      {options.embedding_providers.map(provider => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <select
                      value={formData.embedding_model_name}
                      onChange={(e) => handleModelSelect('embedding_model_name', e.target.value, 'embedding_model_key', options.embedding_models)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Model</option>
                      {formData.embedding_provider && (options.embedding_models[formData.embedding_provider] || []).map(model => (
                        <option key={model.name} value={model.name}>{model.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* API Key field */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                    <span className="ml-2 text-xs text-gray-500">(Required for cloud providers)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showKeys.embedding ? 'text' : 'password'}
                      value={formData.embedding_model_key || ''}
                      onChange={(e) => handleInputChange('embedding_model_key', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter API key"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowKey('embedding')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showKeys.embedding ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint (Optional)</label>
                    <input
                      type="text"
                      value={formData.embedding_model_endpoint || ''}
                      onChange={(e) => handleInputChange('embedding_model_endpoint', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="API endpoint"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Version (Optional)</label>
                    <input
                      type="text"
                      value={formData.embedding_model_apiversion || ''}
                      onChange={(e) => handleInputChange('embedding_model_apiversion', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 2024-02-15-preview"
                    />
                  </div>
                </div>
              </div>

              {/* Vector DB Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Vector Database Configuration</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <select
                      value={formData.vector_db_provider}
                      onChange={(e) => handleInputChange('vector_db_provider', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Provider</option>
                      {options.vector_db_providers.map(provider => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Database</label>
                    <select
                      value={formData.vector_db_name}
                      onChange={(e) => {
                        handleInputChange('vector_db_name', e.target.value);
                        // Auto-fill key, endpoint, and collection_name from vector_dbs config
                        const provider = formData.vector_db_provider;
                        if (provider && options.vector_dbs[provider]) {
                          const selectedDb = options.vector_dbs[provider].find(db => (db.name || db.collection_name) === e.target.value);
                          if (selectedDb) {
                            if (selectedDb.key) {
                              handleInputChange('vector_db_key', selectedDb.key);
                            }
                            if (selectedDb.endpoint) {
                              handleInputChange('vector_db_endpoint', selectedDb.endpoint);
                            }
                            if (selectedDb.collection_name) {
                              handleInputChange('vector_db_collection_name', selectedDb.collection_name);
                            }
                          }
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Database</option>
                      {formData.vector_db_provider && (options.vector_dbs[formData.vector_db_provider] || []).map(db => (
                        <option key={db.name || db.collection_name} value={db.name || db.collection_name}>
                          {db.name || db.collection_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* API Key field */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                    <span className="ml-2 text-xs text-gray-500">(Required for cloud vector databases)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showKeys.vector_db ? 'text' : 'password'}
                      value={formData.vector_db_key || ''}
                      onChange={(e) => handleInputChange('vector_db_key', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter API key"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowKey('vector_db')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showKeys.vector_db ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Collection Name</label>
                    <input
                      type="text"
                      value={formData.vector_db_collection_name || ''}
                      onChange={(e) => handleInputChange('vector_db_collection_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Collection name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Endpoint {formData.vector_db_provider === 'PostgreSQL' ? '(Required)' : '(Optional)'}
                    </label>
                    <input
                      type="text"
                      value={formData.vector_db_endpoint || ''}
                      onChange={(e) => handleInputChange('vector_db_endpoint', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={formData.vector_db_provider === 'PostgreSQL' 
                        ? "postgresql://user:password@host:port/database" 
                        : "API endpoint"}
                    />
                  </div>
                </div>
              </div>

              {/* PDF Parser Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Document Processing</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PDF Parser</label>
                  <select
                    value={formData.pdf_parser}
                    onChange={(e) => handleInputChange('pdf_parser', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {options.pdf_parsers.map(parser => (
                      <option key={parser} value={parser}>{parser}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Vision Model Section */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">Vision Model Configuration</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <select
                      value={formData.vision_llm_provider || ''}
                      onChange={(e) => handleInputChange('vision_llm_provider', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Provider</option>
                      {(options.vision_providers || []).map(provider => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <select
                      value={formData.vision_llm_model_name || ''}
                      onChange={(e) => handleInputChange('vision_llm_model_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Model</option>
                      {formData.vision_llm_provider && (options.vision_models[formData.vision_llm_provider] || []).map(model => (
                        <option key={typeof model === 'string' ? model : model.name} value={typeof model === 'string' ? model : model.name}>
                          {typeof model === 'string' ? model : model.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Settings form
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Documents</label>
                  <input
                    type="number"
                    value={formData.max_documents}
                    onChange={(e) => handleInputChange('max_documents', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max File Size (MB)</label>
                  <input
                    type="number"
                    value={formData.max_file_size_mb}
                    onChange={(e) => handleInputChange('max_file_size_mb', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Temperature</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={formData.default_temperature}
                    onChange={(e) => handleInputChange('default_temperature', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Chunk Size</label>
                  <input
                    type="number"
                    value={formData.default_chunk_size}
                    onChange={(e) => handleInputChange('default_chunk_size', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Chunk Overlap</label>
                  <input
                    type="number"
                    value={formData.default_chunk_overlap}
                    onChange={(e) => handleInputChange('default_chunk_overlap', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformConfigEditModal;